import { Link } from "react-router-dom";
import { ROUTES } from "@/config/routes";
import { isUiMockMode } from "@/config/features";
import { buildExportFromFiltersRequest } from "@/domain/invoices/exportFromFilters";
import {
  formatAmountChip,
  formatDateRangeChip,
  formatDueDateChip,
  validateInvoiceFilterValues,
  type InvoiceFilterValues,
} from "@/domain/invoices/invoiceFilterState";
import { FilterValidationAlert } from "@/features/invoices/components/FilterValidationAlert";
import { useCreateExportFromFilters } from "@/features/invoices/hooks/useCreateExportFromFilters";
import { ApiErrorNotice } from "@/features/billing/components/ApiErrorNotice";

interface BulkExportModalProps {
  open: boolean;
  filters: InvoiceFilterValues;
  documentType?: "invoice_pdf" | "credit_note";
  onClose: () => void;
  onStarted: (jobId: string) => void;
}

export function BulkExportModal({
  open,
  filters,
  documentType = "invoice_pdf",
  onClose,
  onStarted,
}: BulkExportModalProps) {
  const create = useCreateExportFromFilters();
  if (!open) return null;

  const amountChip = formatAmountChip(filters.amountMin, filters.amountMax);
  const dueChip = formatDueDateChip(filters.dueDateFrom, filters.dueDateTo);
  const filterIssues = validateInvoiceFilterValues(filters);
  const canSubmit = filterIssues.length === 0;

  const isCreditNote = documentType === "credit_note";

  const onSubmit = () => {
    if (!canSubmit) return;
    if (isUiMockMode()) {
      onClose();
      onStarted("job_mock_bulk_export");
      return;
    }
    create.mutate(
      buildExportFromFiltersRequest(filters, {
        documentType,
      }),
      {
        onSuccess: (res) => {
          onClose();
          if (res.job_id) onStarted(res.job_id);
        },
      },
    );
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-md rounded-xl border border-[var(--border)] bg-[var(--surface)] p-5 shadow-lg">
        <h2 className="text-lg font-semibold">
          {isCreditNote ? "Start bulk credit note export" : "Start bulk PDF export"}
        </h2>
        <p className="mt-2 text-sm text-[var(--muted)]">
          Stripe scans {isCreditNote ? "credit notes" : "invoices"} in the background. Count is unknown until
          the job runs.
        </p>

        <ul className="mt-4 space-y-1.5 text-sm text-[var(--text-secondary)]">
          <li>
            Customers:{" "}
            <strong>
              {filters.customerIds.length === 0
                ? "All customers"
                : `${filters.customerIds.length} selected`}
            </strong>
          </li>
          <li>
            Status:{" "}
            <strong>
              {filters.statuses.length === 0
                ? "All statuses"
                : filters.statuses.join(", ")}
            </strong>
          </li>
          {amountChip ? (
            <li>
              Amount: <strong>{amountChip}</strong>
            </li>
          ) : null}
          <li>
            {isCreditNote ? "CN created" : "Invoice created"}:{" "}
            <strong>
              {formatDateRangeChip(filters.dateFrom, filters.dateTo)}
            </strong>
          </li>
        {!isCreditNote && dueChip ? (
          <li>
            Due date: <strong>{dueChip}</strong>
          </li>
        ) : null}
        </ul>

        {filterIssues.length > 0 ? (
          <div className="mt-3">
            <FilterValidationAlert messages={filterIssues} />
          </div>
        ) : null}

        {isUiMockMode() ? (
          <p className="mt-4 text-xs text-[var(--text-tertiary)]">
            Mock mode — job is not sent to the API.
          </p>
        ) : (
          <p className="mt-4 text-sm text-[var(--muted)]">
            Track progress and download from{" "}
            <Link to={ROUTES.exports} className="link-quiet">
              Export History
            </Link>
            .
          </p>
        )}

        {create.error ? (
          <ApiErrorNotice error={create.error} context="export" />
        ) : null}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            className="btn-soft"
            onClick={onClose}
            disabled={create.isPending}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn-main"
            onClick={onSubmit}
            disabled={create.isPending || !canSubmit}
          >
            {create.isPending ? "Starting…" : "Start export"}
          </button>
        </div>
      </div>
    </div>
  );
}
